import {
  trapFocus,
  removeTrapFocus,
} from '@shopify/theme-a11y';
import EventHandler from '@pixelunion/events';
import AsyncView from '@pixelunion/shopify-asyncview';
import rimg from '@pixelunion/rimg-shopify';
import { transition } from '@pixelunion/animations';
import serialize from 'form-serialize';
import ScrollLock from '../helpers/ScrollLock';
import QuantitySelector from '../helpers/QuantitySelector';

export default class CartDrawer {
  constructor() {
    this.events = new EventHandler();
    this.overlay = document.querySelector('[data-cart-overlay]');
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.atcButtons = document.querySelectorAll('[data-product-atc]');
    this.cartCount = document.querySelector('[data-header-cart-count]');
    this.recipientForm = document.querySelector('[data-recipient-form]');

    this.trigger = null;

    if (this.atcButtons) {
      this.atcButtons.forEach(button => {
        this.events.register(button, 'click', e => {
          const incompleteForm = button.closest('.product-form--error-option-unselected');
          if (incompleteForm) return;
          e.preventDefault();
          this.trigger = button;
          this.addItem(button);
        });
      });
    }

    document.body.addEventListener('quickshop-add', e => {
      const incompleteForm = e.target.closest('.product-form--error-option-unselected');
      if (incompleteForm) return;
      this.addItem(e.target);
      const quickshopProductURL = e.target.closest('[data-product-url]').dataset.productUrl;
      this.trigger = document.querySelector(`[data-reference-url="${quickshopProductURL}"]`);
    });

    this._closeEsc = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.closeDrawer();
      }
    };

    this.loadDrawer();
  }

  updateCartCount(itemCount) {
    if (this.cartCount) {
      this.cartCount.innerHTML = itemCount;
    }
  }

  addItem(button) {
    const atcButton = button;
    const buttonForm = button.form;
    const formData = serialize(buttonForm, { hash: true });

    if (this.recipientForm) {
      this.recipientForm.classList.remove('recipient-form--has-errors');
    }

    atcButton.classList.add('cart-drawer--loading');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then(response => {
        if (!response.ok) {
          const error = new Error();
          error.response = response;
          throw error;
        }
        return response.json();
      })
      .then(() => {
        this.loadDrawer(true, atcButton);
      })
      .catch(error => this.handleErrors(error, atcButton));
  }

  removeItem(line) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ quantity: 0, line }),
    })
      .then(response => response.json())
      .then(() => {
        this.loadDrawer();
      });
  }

  updateItem(line, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ quantity, line }),
    })
      .then(response => response.json())
      .then(() => {
        this.loadDrawer();
      });
  }

  handleErrors(error, atcButton) {
    error.response.json().then(err => setTimeout(
      () => {
        if (err.errors && err.errors.email) {
          this.recipientForm.classList.add('recipient-form--has-errors');
        }
        atcButton.classList.remove('cart-drawer--loading');
        atcButton.blur();
      },
      500,
    ));
  }

  loadDrawer(forceOpen = false, atcButton = '') {
    AsyncView.load(
      window.PXUTheme.routes.cart_url,
      { view: '_drawer' },
    )
      .then(({ html, data }) => {
        this.drawer.innerHTML = html.content;
        this.initDrawer(forceOpen);
        this.updateCartCount(data.item_count);
        if (atcButton !== '') {
          atcButton.classList.remove('cart-drawer--loading');
          window.modal.close();
        }
      });
  }

  initDrawer(forceOpen) {
    rimg.watch(this.drawer);
    this.drawerOpenButton = document.querySelector('[data-cart-drawer-open]');
    this.drawerCloseButton = document.querySelector('[data-cart-drawer-close]');
    this.inputFields = this.drawer.querySelectorAll('[data-quantity-input]');
    this.removeButtons = this.drawer.querySelectorAll('[data-remove-item]');
    this.animateOverlay = transition({ el: this.overlay, state: 'closed' });
    this.animateDrawer = transition({ el: this.drawer, state: 'closed' });

    this.quantitySelectors = [];

    this.inputFields.forEach(input => {
      this.quantitySelectors.push(new QuantitySelector({
        quantityField: input.parentNode,
        onChange: () => {
          const lineID = input.dataset.lineId;
          const quantity = input.value;
          this.updateItem(lineID, quantity);
        },
      }));
    });

    this.removeButtons.forEach(button => {
      this.events.register(button, 'click', e => {
        e.preventDefault();
        this.removeItem(button.dataset.removeItem);
      });
    });

    this.events.register(this.drawerOpenButton, 'click', e => {
      e.preventDefault();
      this.openDrawer();
      this.trigger = this.drawerOpenButton;
    });

    this.events.register(this.drawerCloseButton, 'click', e => {
      e.preventDefault();
      this.closeDrawer();
    });

    this.events.register(this.overlay, 'click', e => {
      if (e.target.classList.contains('cart-drawer__overlay')) {
        this.closeDrawer();
      }
    });

    if (forceOpen === true) {
      this.openDrawer();
    }
  }

  openDrawer() {
    ScrollLock.lock(this.drawer);
    this.overlay.classList.add('active');
    this.animateOverlay.animateTo('open');
    this.animateDrawer.animateTo('open')
      .then(() => {
        this.closeEscEvent = this.events.register(window, 'keydown', e => this._closeEsc(e));
        trapFocus(this.drawer);
      });
  }

  closeDrawer() {
    ScrollLock.unlock();
    removeTrapFocus(this.drawer);
    this.events.unregister(this.drawerCloseButton);
    this.events.unregister(this.overlay);
    this.events.unregister(this.closeEscEvent);

    this.animateOverlay.animateTo('closed');
    this.animateDrawer.animateTo('closed')
      .then(() => {
        this.overlay.classList.remove('active');
        this.trigger.focus();
      });
  }

  unload() {
    this.quantitySelectors.forEach(selector => selector.unload());
  }
}
